import React, { useEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLanguage } from '../context/LanguageContext';
import matterportService from '../services/matterportService';
import audioService from '../services/audioService';
import { assetUrl } from '../utils/assetUrl';
import { Z_INDEX } from '../const';
import quickNavConfig from '../config/quickNavConfig.json';
import './TourOverlay.css';

// Resolve a tour step → narrationKey slug used in the file name pattern
// `/assets/audio/narration/<key>_<mode>_<lang>.mp3`. Multiple stops may
// share the same `tourSid` (e.g. Mahaveer Bandhara reuses the Mandir
// Entrance sweep), so we disambiguate by the guided-tour step `index`
// — the position inside the same `!excludeFromTour` filter used to
// build the tour order in LocationQuickNav. The sid lookup is kept
// as a safety fallback for callers without an index.
const narrationKeyForStep = (sid, index) => {
  if (typeof index === 'number') {
    const tourLocs = quickNavConfig.locations.filter((l) => !l.excludeFromTour);
    const byIndex = tourLocs[index];
    if (byIndex?.narrationKey) return byIndex.narrationKey;
  }
  return quickNavConfig.locations.find((l) => l.tourSid === sid)?.narrationKey || null;
};

const AUTO_ADVANCE_DELAY = 4000;
const FLY_TRANSITION_DELAY = 1500; // wait for Matterport camera fly before showing images
const MIN_STEP_DWELL = 8000; // minimum time at a step with no images (read description)
const INTER_STEP_PAUSE = 1200; // small breath between steps so the tour feels calm

const TourOverlay = () => {
  const { language } = useLanguage();
  const [stepData, setStepData] = useState(null);
  const [imageIndex, setImageIndex] = useState(0);
  const [slideshowReady, setSlideshowReady] = useState(false);
  const [imagesFinished, setImagesFinished] = useState(false);
  const [audioFinished, setAudioFinished] = useState(false);
  const [paused, setPaused] = useState(false);
  // Keep the last displayed image visible during step transitions to prevent
  // a flash of the 3D view while the next step's image preloads.
  const holdImageRef = useRef(null);
  const holdSidRef = useRef(null);

  const stopNarration = useCallback(() => {
    audioService.stopTourNarration();
    audioService.unduckForTour();
  }, []);

  const playNarration = useCallback((sid, index, lang) => {
    const key = narrationKeyForStep(sid, index);
    if (!key) {
      // Unknown stop — skip narration but don't block tour.
      setAudioFinished(true);
      return;
    }
    const src = assetUrl(`/assets/audio/narration/${key}_connected_${lang}.mp3`);
    audioService.duckForTour();
    audioService.playTourNarration(
      src,
      // onEnded
      () => { audioService.unduckForTour(); setAudioFinished(true); },
      // onError — file missing or failed to load; don't block tour.
      () => { audioService.unduckForTour(); setAudioFinished(true); },
      key,
    );
  }, []);

  // Listen for pause/resume events to gate auto-advance timers
  useEffect(() => {
    const onPause = () => setPaused(true);
    const onResume = () => setPaused(false);
    matterportService.addEventListener('tourPause', onPause);
    matterportService.addEventListener('tourResume', onResume);
    return () => {
      matterportService.removeEventListener('tourPause', onPause);
      matterportService.removeEventListener('tourResume', onResume);
    };
  }, []);

  // ── Reset image index + completion flags when tour step changes ────────────
  useEffect(() => {
    setImageIndex(0);
    setImagesFinished(false);
    setAudioFinished(false);

    const stepConfig = window._tourTranslations?.[stepData?.sid] ?? {};
    const images = stepConfig.images ?? [];
    const flyDelay = (stepConfig.camera?.flyTime ?? FLY_TRANSITION_DELAY) + 300; // fly + small buffer
    let cancelled = false;

      if (images.length > 0) {
        // Camera doesn’t move for image steps — show images as soon as
        // the first one is preloaded (no fly delay needed).
        if (!holdImageRef.current) setSlideshowReady(false);

        const img = new Image();
        img.onload = img.onerror = () => {
          if (!cancelled) {
            holdImageRef.current = null; // new image ready, drop the hold frame
            setSlideshowReady(true);
          }
        };
        img.src = assetUrl(images[0]);

        return () => { cancelled = true; };
    } else {
      // Next step has NO images — clear slideshow immediately so user sees 3D fly
      holdImageRef.current = null;
      setSlideshowReady(false);
      const flyTimer = setTimeout(() => setSlideshowReady(true), flyDelay);
      return () => clearTimeout(flyTimer);
    }
  }, [stepData?.sid]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-advance images: show each for configured imageDuration ──────────────
  useEffect(() => {
    if (!stepData?.sid || !slideshowReady || paused) return;
    const stepConfig = window._tourTranslations?.[stepData.sid] ?? {};
    const images = stepConfig.images ?? [];
    const cam = stepConfig.camera ?? {};
    if (images.length === 0) {
      // No images — wait configured dwellTime so user can view the 3D scene
      const dwell = setTimeout(() => setImagesFinished(true), cam.dwellTime ?? MIN_STEP_DWELL);
      return () => clearTimeout(dwell);
    }
    const timer = setTimeout(() => {
      if (imageIndex < images.length - 1) {
        setImageIndex(imageIndex + 1);
      } else {
        setImagesFinished(true);
      }
    }, cam.imageDuration ?? AUTO_ADVANCE_DELAY);
    return () => clearTimeout(timer);
  }, [imageIndex, stepData?.sid, slideshowReady, paused]);

  // ── Advance tour only when BOTH images and audio are done ──────────────────
  // A small breath (`INTER_STEP_PAUSE`) is added before moving on so the
  // narration's tail isn't cut off and the next camera fly feels intentional.
  useEffect(() => {
    if (paused) return;
    if (imagesFinished && audioFinished) {
      const t = setTimeout(() => matterportService.tourNext(), INTER_STEP_PAUSE);
      return () => clearTimeout(t);
    }
  }, [imagesFinished, audioFinished, paused]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onTourStep = ({ sid, index, snapshot }) => {
      // Capture the current image as a hold frame before switching step
      const prevSid = holdSidRef.current;
      const prevImages = window._tourTranslations?.[prevSid]?.images;
      if (prevImages?.length > 0) {
        holdImageRef.current = assetUrl(prevImages[0]);
      }
      holdSidRef.current = sid;
      const totalSteps = (matterportService._orderedSnapshots || matterportService._tourSnapshots)?.length ?? 0;
      setStepData({ sid, index, snapshot, totalSteps });
    };
    const onTourStop = () => {
      holdImageRef.current = null;
      holdSidRef.current = null;
      setStepData(null);
    };

    matterportService.addEventListener('tourStep', onTourStep);
    matterportService.addEventListener('tourStop', onTourStop);
    return () => {
      matterportService.removeEventListener('tourStep', onTourStep);
      matterportService.removeEventListener('tourStop', onTourStop);
    };
  }, []);

  // Play narration when the step changes (stepData.sid or language changes).
  // For 3D stops we delay until `navTransitionEnd` has fired so the voice
  // doesn't start while the screen is still black. Image-only / skipCamera
  // stops don't trigger the black overlay, so narration starts immediately.
  useEffect(() => {
    if (!stepData?.sid) {
      stopNarration();
      return;
    }
    const stepConfig = window._tourTranslations?.[stepData.sid] ?? {};
    const isImageStop = (stepConfig.images?.length ?? 0) > 0;
    const isSkipCamera = stepConfig.camera?.skipCamera === true;
    if (isImageStop || isSkipCamera) {
      playNarration(stepData.sid, stepData.index, language);
      return;
    }

    // 3D stop: wait for the reveal fade to end before playing audio.
    // If navTransitionEnd doesn't fire within 5 s (e.g. Sweep.moveTo
    // rejected quickly on mobile before React registered the listener),
    // start narration anyway so the tour isn't stuck forever.
    let started = false;
    const start = () => {
      if (started) return;
      started = true;
      playNarration(stepData.sid, stepData.index, language);
    };
    const onEnd = () => start();
    const safety = setTimeout(start, 5000);
    window.addEventListener('navTransitionEnd', onEnd);
    return () => {
      clearTimeout(safety);
      window.removeEventListener('navTransitionEnd', onEnd);
    };
  }, [stepData?.sid, stepData?.index, language, playNarration, stopNarration]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stepData) return null;

  const images = window._tourTranslations?.[stepData.sid]?.images ?? [];
  const hasImages = images.length > 0 && slideshowReady;
  // Show a hold frame from the previous step while the new image preloads
  const showHoldImage = !hasImages && !!holdImageRef.current;

  const slideshow = hasImages ? (
    <div
      className="tour-slideshow"
      style={{ zIndex: Z_INDEX.TOUR_SLIDESHOW }}
      aria-label="Tour slideshow"
    >
      {/* Image with fade on change */}
      <img
        key={imageIndex}
        className="tour-slideshow__img"
        src={assetUrl(images[imageIndex])}
        alt=""
      />

      {/* Counter */}
      {images.length > 1 && (
        <span className="tour-slideshow__counter">{imageIndex + 1} / {images.length}</span>
      )}

      {/* Prev arrow */}
      {images.length > 1 && (
        <button
          type="button"
          className="tour-slideshow__nav tour-slideshow__nav--prev"
          onClick={() => setImageIndex((i) => Math.max(i - 1, 0))}
          aria-label="Previous image"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6"/>
          </svg>
        </button>
      )}

      {/* Next arrow */}
      {images.length > 1 && (
        <button
          type="button"
          className="tour-slideshow__nav tour-slideshow__nav--next"
          onClick={() => setImageIndex((i) => Math.min(i + 1, images.length - 1))}
          aria-label="Next image"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6"/>
          </svg>
        </button>
      )}

      {/* Dots */}
      {images.length > 1 && images.length <= 8 && (
        <div className="tour-slideshow__dots">
          {images.map((_, i) => (
            <button
              key={i}
              type="button"
              className={`tour-slideshow__dot${i === imageIndex ? ' tour-slideshow__dot--active' : ''}`}
              onClick={() => setImageIndex(i)}
              aria-label={`Image ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  ) : null;

  // Transitional hold frame — shows the previous step’s image while the new one preloads
  const holdOverlay = showHoldImage ? (
    <div
      className="tour-slideshow"
      style={{ zIndex: Z_INDEX.TOUR_SLIDESHOW }}
      aria-hidden="true"
    >
      <img className="tour-slideshow__img" src={holdImageRef.current} alt="" />
    </div>
  ) : null;

  return (
    <>
      {holdOverlay && createPortal(holdOverlay, document.body)}
      {slideshow && createPortal(slideshow, document.body)}
    </>
  );
};

export default TourOverlay;
